import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { api } from "../api/client";
import { useBranding } from "../contexts/BrandingContext";
import { ArrowLeft } from "lucide-react";

const DATA_POINTS = {
  id: [
    "Nama lengkap, alamat email, dan nomor WhatsApp",
    "Program & batch yang Anda ikuti serta kode peserta",
    "Bukti transfer pembayaran yang Anda unggah",
    "Foto makanan harian beserta waktu pengambilan foto (metadata EXIF) bila tersedia",
    "Data timbang: berat badan dan persentase body fat (sebelum & sesudah)",
    "Foto progres badan (sebelum & sesudah) yang diunggah oleh Coach Anda",
    "Aktivitas belajar: hari edukasi yang diselesaikan, kuis, refleksi, dan streak",
    "Interaksi komunitas: postingan, komentar, reaksi, dan poin",
    "Bukti keanggotaan Herbalife resmi atau invoice bila Anda mendaftar menjadi Coach",
    "Catatan teknis: waktu login, alamat IP pada percobaan login, dan jejak audit tindakan admin",
  ],
  en: [
    "Full name, email address and WhatsApp number",
    "The programme & batch you joined and your participant code",
    "Payment transfer proofs you upload",
    "Daily food photos and, when available, their capture time (EXIF metadata)",
    "Measurements: body weight and body fat percentage (before & after)",
    "Body progress photos (before & after) uploaded by your Coach",
    "Learning activity: completed education days, quizzes, reflections and streaks",
    "Community activity: posts, comments, reactions and points",
    "Official Herbalife membership proof or invoice if you apply to become a Coach",
    "Technical records: sign-in times, IP addresses on login attempts and admin audit trails",
  ],
};

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
      <div className="text-sm text-slate-300 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export function LegalShell({ testId, title, children }) {
  const { t } = useLang();
  const { branding } = useBranding();
  return (
    <div data-testid={testId} className="min-h-screen bg-[#0B0C13]">
      <div className="mx-auto max-w-3xl px-6 py-12 space-y-8">
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {branding?.app_name || "Fat Loss Program"}
        </Link>
        <h1 className="font-[Outfit] text-4xl sm:text-5xl font-extrabold text-white">{title}</h1>
        {children}
        <p className="text-xs text-slate-500 pt-8 border-t border-white/5">{t("legal.footerNote")}</p>
      </div>
    </div>
  );
}

export function useLegal() {
  const [legal, setLegal] = useState(null);
  useEffect(() => { api.get("/public/legal").then(({ data }) => setLegal(data.data)).catch(() => setLegal({})); }, []);
  return legal || {};
}

export default function Privacy() {
  const { t, lang } = useLang();
  const legal = useLegal();
  const entity = legal.entity_name || "[Nama Usaha]";
  return (
    <LegalShell testId="privacy-page" title={t("legal.privacyTitle")}>
      <p className="text-sm text-slate-400">
        {t("legal.effective")}: {legal.effective_date || "—"} · {t("legal.version")} {legal.version || "1.0"}
      </p>
      <Section title={t("legal.whoWeAre")}>
        <p>{t("legal.whoWeAreBody").replace("{entity}", entity).replace("{city}", legal.city || "[Kota]").replace("{country}", legal.country || "Indonesia")}</p>
        <p>{t("legal.contact")}: {legal.contact_email || "—"}{legal.whatsapp ? ` · WhatsApp ${legal.whatsapp}` : ""}</p>
      </Section>
      <Section title={t("legal.dataWeStore")}>
        <ul data-testid="privacy-data-list" className="list-disc pl-5 space-y-1">
          {(DATA_POINTS[lang] || DATA_POINTS.id).map((d) => <li key={d}>{d}</li>)}
        </ul>
      </Section>
      <Section title={t("legal.whyWeStore")}><p>{t("legal.whyWeStoreBody")}</p></Section>
      <Section title={t("legal.whoSees")}><p>{t("legal.whoSeesBody")}</p></Section>
      <Section title={t("legal.retention")}><p>{t("legal.retentionBody")}</p></Section>
      <Section title={t("legal.yourRights")}>
        <p>{t("legal.yourRightsBody").replace("{email}", legal.contact_email || "—")}</p>
      </Section>
      <Section title={t("legal.security")}><p>{t("legal.securityBody")}</p></Section>
    </LegalShell>
  );
}
